import { Suspense, useMemo } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Welcome from "./pages/welcome/Welcome";
import { useAuth } from "./hooks/useAuth";
import { Toaster } from "sonner";
import TermsConditions from "./pages/terms/TermsConditions";
import NotRegistered from "./pages/NotRegistered";
import UserDeactivated from "./pages/UserDeactivated";
import { PermissionProvider } from "./contexts/PermissionContext";
import PersistentLayout from "./components/PersistentLayout";
import { AppContent } from "./components";
import ErrorBoundary from "./components/ErrorBoundary";

// Loading spinner optimizado
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-screen bg-gray-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Páginas que no necesitan layout
const PublicPages = () => (
  <Routes>
    <Route path="/terms" element={<TermsConditions />} />
    <Route path="/welcome" element={<Welcome />} />
    <Route path="/not-registered" element={<NotRegistered />} />
    <Route path="/user-deactivated" element={<UserDeactivated />} />
    <Route path="*" element={<Navigate to="/welcome" replace />} />
  </Routes>
);

// App principal con layout persistente
const AuthenticatedApp = () => (
  <ErrorBoundary>
    <PermissionProvider>
      <PersistentLayout>
        <AppContent />
      </PersistentLayout>
    </PermissionProvider>
  </ErrorBoundary>
);

export default function App() {
  const { isAuthenticated, isLoading, userStatus } = useAuth();

  // Solo loading de autenticación (Auth0) - no incluir userDataLoading
  // para evitar re-renders del sidebar
  const isAppLoading = useMemo(() => isLoading, [isLoading]);

  // Memoizar el estado de redirección para evitar re-renders
  const shouldRedirect = useMemo(() => {
    if (userStatus === "not_registered") return "/not-registered";
    if (userStatus === "inactive") return "/user-deactivated";
    return null;
  }, [userStatus]);

  // Loading state - solo mientras se inicializa la autenticación
  if (isAppLoading) {
    return <LoadingSpinner />;
  }

  // Redirecciones automáticas basadas en estado del usuario
  if (shouldRedirect) {
    return (
      <>
        <Toaster />
        <Routes>
          <Route path="/not-registered" element={<NotRegistered />} />
          <Route path="/user-deactivated" element={<UserDeactivated />} />
          <Route path="*" element={<Navigate to={shouldRedirect} replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <Toaster />
      <Suspense fallback={<LoadingSpinner />}>
        {!isAuthenticated ? <PublicPages /> : <AuthenticatedApp />}
      </Suspense>
    </>
  );
}
